import React, { useMemo, useRef, useState } from "react"
import { PanelSkeleton } from "components/new-stuff/ui/Skeleton"
import { Button, Collapse } from "antd"
import type { CollapseProps, FormInstance } from "antd"
import { useMutation, useQueryClient } from "@tanstack/react-query"
import { Button as CustomButton } from "components"
import buildingIcon from "assets/svg/Buildings.svg"
import geofenceIcon from "assets/svg/MapPinArea.svg"
import globeIcon from "assets/svg/globe.svg"
import checkIcon from "assets/svg/check.svg"
import { useGet } from "utils/useFetch"
import { ewaAxiosInstance } from "utils/request"
import { onQueryError } from "utils/utils"
import { useAppNotificationStore } from "stores"
import CreateShiftPlanConfiguration from "../setup/components/CreateShiftPlanConfiguration"
import type {
  HardwareDevices,
  IShiftPlanDoc,
  IShiftPlansRes,
  IWorkplaceByIdRes,
} from "../typings"
import { SETUP_WORKPLACE_QUERY_KEY } from "../typings"
import { ChevronRightIcon } from "@heroicons/react/24/outline"
import { useNavigate, useSearchParams } from "react-router-dom"
import SelectDutyLocationWrapper from "./SelectDutyLocationWrapper"
import {
  buildShiftPlanBody,
  shiftPlanToConfigInitialValues,
} from "../utils/shiftTablesUtils"
import { CustomBackButton } from "../setup/Setup"
import { SetupWorkplace } from "../setup/components/SetupWorkplaceList"

// ─── Types ────────────────────────────────────────────────────────────────────

interface SetupSummaryProps {
  onBack: () => void
  workplaceId?: string
  onEditWorkplace?: (workplace: SetupWorkplace) => void
  saveToDraft?: () => void
  isDrafting: boolean
}

interface DutyLocationItem {
  id: number | "workplace"
  name: string
  addressLine1?: string
}

// ─── Sub-components ───────────────────────────────────────────────────────────

const EditButton: React.FC<{ onClick?: () => void }> = ({ onClick }) => (
  <button
    type="button"
    className="self-start text-[#004AF5] text-sm font-semibold flex items-center gap-1"
    onClick={onClick}
  >
    Edit
    <ChevronRightIcon className="w-4 h-4 text-[#004AF5]" />
  </button>
)

const formatHardwareDeviceType = (value: string | null | undefined): string => {
  if (!value?.trim()) return "Hardware device"
  return value
    .split(", ")
    .map((item) => {
      const formatted = item.trim().replace(/_/g, " ").toLowerCase()
      return formatted.charAt(0).toUpperCase() + formatted.slice(1)
    })
    .join(", ")
}

const ShiftPlanPanel: React.FC<{
  plan: IShiftPlanDoc
  onEditClick?: () => void
}> = ({ plan, onEditClick }) => {
  const geofencingLabel = plan.allowGeoFencing
    ? `${plan.geofencingOffsetMeters ?? 0}m `
    : "—"

  return (
    <div className="flex flex-col gap-3">
      <div className="grid grid-cols-4 gap-4 text-sm">
        <div className="flex flex-col gap-1.5 border-r border-[#E2E8F0]">
          <p className="font-semibold text-[#0F172B]">Clock-in methods</p>
          <span className="flex items-center gap-1 text-slate-900">
            <img
              src={globeIcon}
              width={16}
              height={16}
              alt=""
            />
            {plan.linkHardwareDevice
              ? formatHardwareDeviceType(
                  plan?.hardwareDeviceTypes
                    ?.map((d: string | HardwareDevices) =>
                      typeof d === "string" ? d : d.deviceType
                    )
                    ?.join(", ")
                )
              : "Web/Mobile"}
          </span>
        </div>

        <div className="flex flex-col gap-1.5 border-r border-[#E2E8F0]">
          <p className="font-semibold text-[#0F172B]">Geofencing</p>
          <span className="flex items-center gap-1 text-slate-900">
            <img
              src={geofenceIcon}
              width={16}
              height={16}
              alt=""
            />
            {geofencingLabel}
          </span>
        </div>

        <div className="flex flex-col gap-1.5 border-r border-[#E2E8F0]">
          <p className="font-semibold text-[#0F172B]">Manual clock-in</p>
          <span className="flex items-center gap-1 text-slate-900">
            {plan.allowManualTimeEntry ? (
              <img
                src={checkIcon}
                width={16}
                height={16}
                alt=""
              />
            ) : (
              <svg
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 24 24"
                strokeWidth="1.5"
                stroke="currentColor"
                className="w-4 h-4"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  d="M6 18 18 6M6 6l12 12"
                />
              </svg>
            )}
            {plan.allowManualTimeEntry ? "Enabled" : "Disabled"}
          </span>
        </div>

        <div className="flex flex-col gap-1.5">
          <p className="font-semibold text-[#0F172B]">Use in payroll</p>
          <span className="flex items-center gap-1 text-slate-900">
            {plan.useInPayrollCalculation ? (
              <img
                src={checkIcon}
                width={16}
                height={16}
                alt=""
              />
            ) : (
              <svg
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 24 24"
                strokeWidth="1.5"
                stroke="currentColor"
                className="w-4 h-4"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  d="M6 18 18 6M6 6l12 12"
                />
              </svg>
            )}
            {plan.useInPayrollCalculation ? "Enabled" : "Disabled"}
          </span>
        </div>
      </div>

      <EditButton onClick={onEditClick} />
    </div>
  )
}

// ─── Component ────────────────────────────────────────────────────────────────

const SetupSummary: React.FC<SetupSummaryProps> = ({
  onBack,
  workplaceId,
  onEditWorkplace,
  saveToDraft,
  isDrafting,
}) => {
  const [searchParams, setSearchParams] = useSearchParams()
  const rawDutyLocationFromUrl = searchParams.get("dutylocationId")
  const selectedDutyLocationId = useMemo(() => {
    if (rawDutyLocationFromUrl === "workplace" || !rawDutyLocationFromUrl)
      return "workplace"
    return Number(rawDutyLocationFromUrl)
  }, [rawDutyLocationFromUrl])

  const [editingPlanId, setEditingPlanId] = useState<number | null>(null)

  const handleDutyLocationChange = (value: string | number) => {
    const nextParams = new URLSearchParams(searchParams)
    nextParams.set("dutylocationId", String(value))
    setSearchParams(nextParams, { replace: true })
  }

  const configFormRef = useRef<FormInstance>(null)
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const { toast } = useAppNotificationStore()

  // ── Workplace ──────────────────────────────────────────────────────────────

  const { data: workplaceRes, isLoading: isWorkplaceLoading } =
    useGet<IWorkplaceByIdRes>({
      url: `/v1/time-attendance/workplaces/${workplaceId ?? ""}`,
      queryKey: [SETUP_WORKPLACE_QUERY_KEY, workplaceId ?? ""],
      instanceKey: "ewaAxiosInstance",
      enabled: !!workplaceId,
      refetchOnMount: false,
    })

  const workplace = workplaceRes?.data
  const dutyLocations = workplace?.officeDutyLocations ?? []

  const primaryWorkplaceLabel =
    workplace?.displayName ?? workplace?.fullAddress ?? ""

  const dutyLocationOptions: DutyLocationItem[] = [
    ...(primaryWorkplaceLabel
      ? [
          {
            id: "workplace" as const,
            name: primaryWorkplaceLabel,
            addressLine1: workplace?.fullAddress ?? "",
          },
        ]
      : []),
    ...dutyLocations.map((loc) => ({
      id: loc.id,
      name: loc.name,
      addressLine1: loc.addressLine1 ?? "",
    })),
  ]

  // ── Shift plans ────────────────────────────────────────────────────────────

  const shiftPlansDutyLocationParam =
    selectedDutyLocationId === "workplace" ? "null" : selectedDutyLocationId

  const shiftPlansQueryParams = new URLSearchParams({
    dutylocationId: String(shiftPlansDutyLocationParam),
    ...(workplaceId != null && { workplaceId: String(workplaceId) }),
  }).toString()

  const shiftPlansUrl = `/v1/time-attendance/shift-plans?${shiftPlansQueryParams}&populate=hardwareDeviceTypes`

  const { data: shiftPlansRes, isLoading: isShiftPlansLoading } =
    useGet<IShiftPlansRes>({
      url: shiftPlansUrl,
      instanceKey: "ewaAxiosInstance",
      enabled: !!workplaceId,
    })

  const shiftPlans: IShiftPlanDoc[] = shiftPlansRes?.data?.docs ?? []
  const editingPlan = shiftPlans.find((p) => p.id === editingPlanId) ?? null

  // ── Mutations ──────────────────────────────────────────────────────────────

  const invalidateShiftPlans = () =>
    queryClient.invalidateQueries({ queryKey: [shiftPlansUrl] })

  const { mutate: publishSetup, isLoading: isPublishing } = useMutation({
    mutationFn: async () => {
      if (!workplaceId) throw new Error("No workplace configuration to publish")
      const res = await ewaAxiosInstance.put(
        `/v1/time-attendance/workplace-config/${workplaceId}`,
        { status: "published", configDone: true }
      )
      return res.data
    },
    onSuccess: () => {
      if (workplaceId) {
        queryClient.invalidateQueries({
          queryKey: [SETUP_WORKPLACE_QUERY_KEY, workplaceId],
        })
      }
      invalidateShiftPlans()
      toast.success("Time & Attendance setup published successfully")
      navigate("/dashboard/time-and-attendance/overview")
    },
    onError: onQueryError,
  })

  const { mutate: updateShiftPlan, isLoading: isUpdatingPlan } = useMutation({
    mutationFn: async ({
      id,
      body,
    }: {
      id: number
      body: Record<string, unknown>
    }) => {
      const res = await ewaAxiosInstance.put(
        `/v1/time-attendance/shift-plans/${id}`,
        body
      )
      return res.data
    },
    onSuccess: () => {
      invalidateShiftPlans()
      toast.success("Shift plan updated successfully")
      setEditingPlanId(null)
    },
    onError: onQueryError,
  })

  // ── Handlers ───────────────────────────────────────────────────────────────

  const handleSaveConfig = async () => {
    if (!editingPlan) return
    try {
      await configFormRef.current?.validateFields()
      const config = configFormRef.current?.getFieldsValue() ?? {}
      updateShiftPlan({ id: editingPlan.id, body: buildShiftPlanBody(config) })
    } catch {
      // Validation failed — form will display field errors
    }
  }

  // ── Collapse items ─────────────────────────────────────────────────────────

  const collapseItems: CollapseProps["items"] = shiftPlans.map((plan) => ({
    key: String(plan.id),
    label: <p className="font-semibold text-[#0F172B]">{plan.title}</p>,
    children:
      editingPlanId === plan.id ? (
        <div className="flex flex-col gap-3">
          <div className="flex justify-end">
            <button
              type="button"
              className="text-sm text-[#64748B] hover:text-[#0F172B] underline"
              onClick={() => setEditingPlanId(null)}
            >
              Back to summary
            </button>
          </div>
          <CreateShiftPlanConfiguration
            key={`summary-config-${plan.id}`}
            shiftPlanName={plan.title}
            formRef={configFormRef}
            initialValues={{
              ...shiftPlanToConfigInitialValues(plan),
              hardwareDeviceTypes: plan.hardwareDeviceTypes
                ? (plan.hardwareDeviceTypes.map((d: string | HardwareDevices) =>
                    typeof d === "string" ? d : d.deviceType
                  ) as (string | HardwareDevices)[])
                : [],
            }}
            mode="edit"
            onSave={handleSaveConfig}
            saveLoading={isUpdatingPlan}
          />
        </div>
      ) : (
        <ShiftPlanPanel
          plan={plan}
          onEditClick={() => setEditingPlanId(plan.id)}
        />
      ),
  }))

  // ── Render ─────────────────────────────────────────────────────────────────

  return (
    <>
      {/* Header (static) */}
      <div className="flex flex-col gap-4 shrink-0">
        <CustomBackButton onBack={onBack} />

        <div className="flex flex-col gap-1">
          <h5 className="text-2xl text-[#0F172B] font-bold">
            Review your Time &amp; Attendance setup
          </h5>
          <p className="text-[#64748B] text-sm">
            Check your configuration below. You can edit any section before
            publishing or save as a draft to finish later.
          </p>
        </div>
      </div>

      {/* Scrollable content */}
      <div className="flex-1 min-h-0 overflow-y-auto flex flex-col gap-6 pt-6 pr-2">
        {/* Workplace */}
        <div className="flex flex-col gap-2">
          <h4 className="font-bold text-lg text-[#0F172B]">Workplace</h4>
          {isWorkplaceLoading ? (
            <PanelSkeleton />
          ) : workplace ? (
            <div className="border-l-2 border-[#E2E8F0] pl-3 flex flex-col gap-1">
              <p className="font-semibold text-sm text-[#0F172B]">
                {workplace.displayName || "Workplace"}
              </p>
              <span className="flex items-center gap-1.5 text-sm">
                <img
                  src={buildingIcon}
                  width={16}
                  height={16}
                  alt=""
                />
                <strong>Primary location:</strong>{" "}
                {workplace.fullAddress || workplace.addressLine1 || "—"}
              </span>
              <EditButton
                onClick={() => {
                  if (!workplace || !onEditWorkplace) return
                  onEditWorkplace!({
                    id: String(workplace.id),
                    name: workplace.displayName ?? "",
                    description: workplace.displayName ?? "",
                    address:
                      workplace.fullAddress ?? workplace.addressLine1 ?? "",
                    latitude: workplace.latitude ?? null,
                    longitude: workplace.longitude ?? null,
                    city: workplace?.city ?? "",
                    country: workplace.country ?? "",
                    state: workplace.state ?? "",
                    taxOfficeName: workplace.taxOfficeName ?? "",
                    taxOfficeCode: workplace.taxOfficeCode ?? "",
                    nhfOfficeName: workplace.nhfOfficeName ?? "",
                    nhfOfficeCode: workplace.nhfOfficeCode ?? "",
                    workplaceConfig: workplace.workplaceConfig ?? null,
                  })
                }}
              />
            </div>
          ) : (
            <div className="border-l-2 border-[#E2E8F0] pl-3">
              <p className="text-sm text-[#64748B]">No workplace selected</p>
            </div>
          )}
        </div>

        {/* Shift plans */}
        <div className="flex flex-col gap-3">
          <SelectDutyLocationWrapper
            dutyLocation={dutyLocationOptions}
            isLoadingDutyLocation={isWorkplaceLoading}
            selectedLocation={selectedDutyLocationId}
            setSelectedLocation={handleDutyLocationChange}
          />

          {isShiftPlansLoading ? (
            <PanelSkeleton />
          ) : collapseItems.length > 0 ? (
            <div className="border-l-2 border-[#E2E8F0] pl-3">
              <Collapse
                accordion
                defaultActiveKey={
                  collapseItems[0]?.key ? [collapseItems[0].key as string] : []
                }
                expandIcon={() => (
                  <ChevronRightIcon className="w-4 h-4 rotate-90" />
                )}
                items={collapseItems}
                expandIconPosition="end"
                bordered={false}
                className="bg-white [&_.ant-collapse]:!border-none [&_.ant-collapse]:!bg-transparent [&_.ant-collapse]:!box-shadow-none [&_.ant-collapse-item]:!border [&_.ant-collapse-item]:!border-slate-200 [&_.ant-collapse-item]:!rounded-md [&_.ant-collapse-item]:!box-shadow-none [&_.ant-collapse-header]:!border-none [&_.ant-collapse-header]:!bg-transparent [&_.ant-collapse-content]:!border-none [&_.ant-collapse-content]:!bg-transparent [&_.ant-collapse-content]:!box-shadow-none [&_.ant-collapse-content-box]:!bg-transparent [&_*]:!shadow-none [&_.ant-collapse-item]:mb-2 [&_.ant-collapse-item]:overflow-hidden [&_.ant-collapse-content-box]:pt-0"
              />
            </div>
          ) : (
            <p className="text-sm text-[#64748B] py-4">
              No shift plans for this duty location.
            </p>
          )}
        </div>
      </div>

      {/* Footer */}
      <div className="shrink-0 flex justify-end items-center gap-3 pt-4 border-t border-[#E2E8F0]">
        <Button
          type="default"
          className="font-semibold h-10 rounded-lg border-2 border-[#E2E8F0] text-[#0F172B]"
          onClick={saveToDraft}
          loading={isDrafting}
        >
          Save to drafts
        </Button>
        <CustomButton
          color="primary"
          className="font-semibold h-10"
          onClick={() => publishSetup()}
          submitting={isPublishing}
          disabled={!workplaceId || isDrafting}
        >
          Publish setup
        </CustomButton>
      </div>
    </>
  )
}

export default SetupSummary
